import {
  ListChecks,
  History,
  Settings,
  Cpu,
  ShieldCheck,
  BookOpen,
  Building2,
  FileText,
  SlidersHorizontal,
  ClipboardList,
  Layers,
  Activity,
  Megaphone,
  KeyRound,
  Bot,
  type LucideIcon,
} from 'lucide-react'
import type { AccessGates, ModuleKey } from '@/shared/rbac'

export interface NavItem {
  label: string
  icon: LucideIcon
  path: string
  /**
   * Post-refactor: the module slug that gates visibility of this nav item.
   * The sidebar shows the item iff `useAccess().can(module, 'read')` is true.
   * Preferred over the legacy `access` gate string.
   */
  module?: ModuleKey
  /**
   * Legacy: named access gate from useAccess(). Still supported for items
   * whose visibility depends on a composite condition (multi-module OR).
   * If both `module` and `access` are set, `module` wins.
   */
  access?: keyof AccessGates
  /**
   * When true, the item is only shown to superadmins (system_admin:edit:all),
   * on top of any `module`/`access` gate. Non-superadmins never see it.
   */
  superadminOnly?: boolean
  /**
   * When true, the sidebar shows a small unread dot on this item while the user
   * has unread announcements (drives off the updates unread-count query).
   */
  unreadDot?: boolean
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

/**
 * Section ordering — top to bottom:
 *   1. Workspace          — daily coding work + manager notes
 *   2. Dashboards         — role-specific admin landings (Client / Engine)
 *   3. Knowledge Base     — reference data the platform exposes
 *   4. Clients            — cross-client directory (Super Admin lookup)
 *   5. System             — platform administration (audit logs live under /system-admin/audit-logs)
 *
 * Note: Notifications page (`/notifications`) exists but is intentionally not in the
 * sidebar. Entry point is the bell icon in the top header → "View all notifications".
 *
 * Access gates match the route guards in app/router.tsx so an item never
 * appears in the sidebar unless the user can actually load the page behind it.
 */
export const navigation: NavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { label: 'Chart Queue', icon: ListChecks, path: '/workspace/queue', module: 'chart_queue' },
      { label: 'History', icon: History, path: '/workspace/history', module: 'history' },
      { label: 'Notes', icon: FileText, path: '/workspace/notes', module: 'notes' },
    ],
  },
  {
    label: 'Dashboards',
    items: [
      { label: 'Charts Dashboard', icon: Settings, path: '/client-admin/overview', module: 'charts_dashboard' },
      { label: 'Operations Dashboard', icon: Activity, path: '/operations-dashboard', module: 'operations_dashboard' },
      { label: 'Analytics Dashboard', icon: Cpu, path: '/engine-admin/overview', module: 'analytics_dashboard' },
    ],
  },
  {
    label: 'Master Library',
    items: [
      { label: 'Knowledge Base', icon: BookOpen, path: '/kb', module: 'knowledge_base' },
      { label: 'Mandatory Fields', icon: ClipboardList, path: '/platform-config/mandatory-fields', module: 'mandatory_fields' },
      { label: 'Prompt Library', icon: SlidersHorizontal, path: '/prompt-config', module: 'prompt_config' },
      { label: 'Configurations', icon: Layers, path: '/client-config', module: 'client_config' },
      // Docs for the internal MCP (all domains). Visible only to accounts that
      // can actually use the MCP (`mcp` module) — same rule as every other item.
      { label: 'MCP Playbook', icon: Bot, path: '/mcp-book', module: 'mcp' },
    ],
  },
  {
    label: 'Clients',
    items: [
      { label: 'Clients', icon: Building2, path: '/clients', module: 'clients' },
    ],
  },
  {
    label: 'Access',
    items: [
      { label: 'Access Requests', icon: KeyRound, path: '/access', module: 'access_request' },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'System Admin', icon: ShieldCheck, path: '/system-admin/clients', module: 'system_admin' },
      // Announcements — gated by the composite `canViewAnnouncements` gate
      // (updates read + the announcements_module feature flag), so it stays hidden
      // while the kill-switch is off. Sits below System Admin per latest UX.
      // The internal module key stays `updates`; only the display label changed.
      { label: 'Announcements', icon: Megaphone, path: '/announcements', access: 'canViewAnnouncements', unreadDot: true },
    ],
  },
]
